import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { ScriptOnce } from "@tanstack/react-router";
import { ThemeProvider, createThemeInitScript } from "theme-toggle-kit";
import { useEffect, useMemo, type ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n, { isLang, DEFAULT_LANG, ensureLang, serializeBundle, type Lang } from "../i18n";
import { namespacesForPath } from "@/lib/i18n-namespaces";

import appCss from "../styles.css?url";

import montserratLatinWoff2 from "@fontsource-variable/montserrat/files/montserrat-latin-wght-normal.woff2?url";
import outfitLatinWoff2 from "@fontsource-variable/outfit/files/outfit-latin-wght-normal.woff2?url";
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { SmoothScrollProvider } from "@/components/smooth-scroll-provider";
import { RevealRoot } from "@/components/reveal-root";
import { CookieConsent } from "@/components/cookie-consent";
import { GtmLoader } from "@/components/gtm-loader";
import { LinkClickTracker } from "@/components/link-click-tracker";
import { ScrollDepthTracker } from "@/components/scroll-depth-tracker";
import { WebVitalsReporter } from "@/components/web-vitals-reporter";

import { DeferredWidgets } from "@/components/deferred-widgets";

const THEME_STORAGE_KEY = "itsolutions.theme";
// Licht is de standaard huisstijl (Smarter Digital). Een handmatige keuze via de
// thema-toggle wordt opgeslagen en wint van deze standaard.
const THEME_DEFAULT = "light" as const;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Laadt de resources van uitsluitend de actieve taal voordat er iets
  // rendert — zowel bij SSR als bij client-navigatie.
  beforeLoad: async ({ location }) => {
    await ensureLang(detectLangFromPath(location.pathname));
  },
  head: () => {
    // Inline @font-face rules with the *hashed* woff2 URLs so the browser
    // can bind them to the <link rel="preload"> below immediately — no
    // wait for styles.css. Same URL as the preload → single fetch.
    const fontFaceCss = `@font-face{font-family:"Montserrat Variable";font-style:normal;font-display:swap;font-weight:300 900;src:url("${montserratLatinWoff2}") format("woff2-variations");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}@font-face{font-family:"Outfit Variable";font-style:normal;font-display:swap;font-weight:100 900;src:url("${outfitLatinWoff2}") format("woff2-variations");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}@font-face{font-family:"Inter Variable";font-style:normal;font-display:swap;font-weight:100 900;src:url("${interLatinWoff2}") format("woff2-variations");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}`;
    // The main stylesheet is render-blocking on purpose. It is only ~18 KiB
    // and arrives in parallel with the fonts, while loading it asynchronously
    // caused a ~0.38 CLS: the first paint had no Tailwind utilities, so the
    // whole page re-laid-out once the CSS was promoted.
    const styleLinks = [{ rel: "stylesheet" as const, href: appCss }];
    return {
      meta: [
        { charSet: "utf-8" },
        // Laat de browser de UA-achtergrond meteen goed kleuren (geen witte flits
        // op donkere systemen voordat CSS/JS geladen is).
        { name: "color-scheme", content: "light dark" },
        { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
        {
          name: "google-site-verification",
          content: "6_J8ePQsNqpHGkeCuNSZbc_za3l1p1YesmsJ4kkbZr8",
        },
        { title: "Onafhankelijk digitaal advies & SEO-audit — IT Solutions" },
        {
          name: "description",
          content:
            "Onafhankelijke beoordeling van uw SEO, GEO en advertentiebudget. Eerst meten en adviseren, pas daarna bouwen. Rapport en actieplan in 2–3 weken.",
        },
        { name: "author", content: "IT Solutions" },
        {
          property: "og:title",
          content: "Onafhankelijk digitaal advies & SEO-audit — IT Solutions",
        },
        {
          property: "og:description",
          content:
            "Onafhankelijke beoordeling van uw SEO, GEO en advertentiebudget. Eerst meten en adviseren, pas daarna bouwen. Rapport en actieplan in 2–3 weken.",
        },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://www.itsolutions.be/og-image.jpg?v=7" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "Onafhankelijk digitaal advies — IT Solutions" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Onafhankelijk digitaal advies — IT Solutions" },
        {
          name: "twitter:description",
          content:
            "Onafhankelijke audit van uw complete digitale marketing: SEO, GEO, Meta- en Google-advertentiebudgetten en online rendement. Eerst objectief meten en adviseren, pas daarna bouwen. Rapport en actieplan in 2–3 weken.",
        },
        { name: "twitter:image", content: "https://www.itsolutions.be/og-image.jpg?v=7" },
        { name: "twitter:image:alt", content: "Onafhankelijk digitaal advies — IT Solutions" },
      ],
      links: [
        // Main stylesheet — render-blocking (see note above).
        ...styleLinks,
        // Preload the two latin variable-font woff2's so they start
        // downloading in parallel with styles.css instead of after it.
        // Same URL as the @font-face src in styles.css — no double fetch.
        {
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: montserratLatinWoff2,
          crossOrigin: "anonymous",
        },
        {
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: outfitLatinWoff2,
          crossOrigin: "anonymous",
        },
        {
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: interLatinWoff2,
          crossOrigin: "anonymous",
        },
        // De fonts zijn self-hosted (zelfde origin), dus daarvoor is preconnect
        // zinloos. Wat wél een externe verbinding kost, is GTM/GA4: de TLS- en
        // DNS-onderhandeling daarvan wordt hiermee alvast opgezet, zodat de
        // uitgestelde containerload later geen extra RTT's meer kost.
        { rel: "preconnect", href: "https://www.googletagmanager.com", crossOrigin: "anonymous" },
        { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
        { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
        { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      ],
      // Only the @font-face rules are inlined (with hashed URLs) so the
      // preloaded woff2's bind immediately. All other CSS comes from the
      // render-blocking stylesheet above.
      styles: [{ children: fontFaceCss }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://www.itsolutions.be/#organization",
                name: "IT Solutions",
                legalName: "ITsolutions bv",
                url: "https://www.itsolutions.be/",
                logo: {
                  "@type": "ImageObject",
                  "@id": "https://www.itsolutions.be/#logo",
                  url: "https://www.itsolutions.be/logo.png",
                  contentUrl: "https://www.itsolutions.be/logo.png",
                  width: 512,
                  height: 512,
                  caption: "IT Solutions",
                },
                image: { "@id": "https://www.itsolutions.be/#logo" },
                email: "hello@itsolutions.be",
                telephone: "+32475950335",
                vatID: "BE0463829848",
                taxID: "BE0463829848",
                // sameAs: voeg hier publieke bedrijfsprofielen toe zodra ze bestaan
                // (LinkedIn company, Facebook page, Instagram, Google Business Profile,
                // YouTube kanaal, X/Twitter, etc.). Google's entity graph negeert het
                // hele veld bij verzonnen of dode links — leeglaten is beter dan raden.
                sameAs: [],
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "Centrum Zuid 1527e",
                  postalCode: "3530",
                  addressLocality: "Houthalen-Helchteren",
                  addressCountry: "BE",
                },
                contactPoint: [
                  {
                    "@type": "ContactPoint",
                    contactType: "sales",
                    email: "hello@itsolutions.be",
                    telephone: "+32475950335",
                    availableLanguage: ["nl", "fr", "en"],
                    areaServed: "BE",
                  },
                  {
                    "@type": "ContactPoint",
                    contactType: "customer support",
                    email: "hello@itsolutions.be",
                    availableLanguage: ["nl", "fr", "en"],
                    areaServed: "BE",
                  },
                ],
                parentOrganization: {
                  "@type": "Organization",
                  "@id": "https://www.itsolutions.be/#parent-organization",
                  name: "ITsolutions bv",
                  vatID: "BE0463829848",
                  taxID: "BE0463829848",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "Centrum Zuid 1527e",
                    postalCode: "3530",
                    addressLocality: "Houthalen-Helchteren",
                    addressCountry: "BE",
                  },
                },
              },
              {
                "@type": "WebSite",
                "@id": "https://www.itsolutions.be/#website",
                url: "https://www.itsolutions.be/",
                name: "IT Solutions",
                inLanguage: ["nl-BE", "fr-BE", "en"],
                publisher: { "@id": "https://www.itsolutions.be/#organization" },
              },
            ],
          }),
        },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent as unknown as false,
});

function RootShell({ children }: { children: ReactNode }) {
  // The <html lang> attribute reflects the current URL's language segment,
  // derived from the router's active location so SSR and client agree.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const initialLang = detectLangFromPath(pathname);
  return (
    <html lang={initialLang} suppressHydrationWarning>
      <head>
        {/* Runs before first paint: restores the saved light/dark preference, no flash. */}
        <ScriptOnce>
          {createThemeInitScript({ storageKey: THEME_STORAGE_KEY, defaultTheme: THEME_DEFAULT })}
        </ScriptOnce>
        <HeadContent />
        {/* Resources van de actieve taal: laat de eerste client-render
            dezelfde vertalingen gebruiken als de server, zonder de andere
            talen mee te bundelen. */}
        <script
          id="i18n-data"
          type="application/json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: serializeBundle(initialLang, namespacesForPath(pathname, initialLang)),
          }}
        />

      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function detectLangFromPath(pathname: string): Lang {
  const first = pathname.split("/").filter(Boolean)[0];
  return isLang(first) ? (first as Lang) : DEFAULT_LANG;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider storageKey={THEME_STORAGE_KEY} defaultTheme={THEME_DEFAULT}>
        <I18nextProvider i18n={i18n}>
          <LangEffects />
          <SmoothScrollProvider>
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
            <RevealRoot />
            <Toaster />
            <CookieConsent />
            <GtmLoader />
            <LinkClickTracker />
            <ScrollDepthTracker />
            <WebVitalsReporter />
            <DeferredWidgets />
          </SmoothScrollProvider>
        </I18nextProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

/**
 * Keeps i18n language in sync with the URL and runs the one-shot
 * Accept-Language auto-detect for first-time visitors on the NL root.
 */
function LangEffects() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const router = useRouter();
  const lang = useMemo(() => detectLangFromPath(pathname), [pathname]);

  useEffect(() => {
    if (i18n.resolvedLanguage !== lang && i18n.language !== lang) void i18n.changeLanguage(lang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (pathname !== "/") return;
    if (document.cookie.includes("i18nextLng=")) return;
    // First-time visitor on NL root — try Accept-Language and jump once.
    void fetch("/api/detect-lang")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.lang || data.lang === DEFAULT_LANG || !isLang(data.lang)) return;
        document.cookie = `i18nextLng=${data.lang}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
        router.navigate({ to: `/${data.lang}` });
      })
      .catch(() => {});
  }, [pathname, router]);

  return null;
}
